import { useEffect, useId } from 'react';
import { create } from 'zustand';

import { withKey } from './key-set';

/** How often an open alignment asks for the other side's changes while the page is visible. */
export const LIVE_REFRESH_INTERVAL_MS = 5000;

type LiveRefreshPauseState = {
    /** Who holds which alignment, as `alignmentId:holder`. */
    readonly pausedBy: ReadonlySet<string>;
    readonly setPaused: (key: string, paused: boolean) => void;
};

const useLiveRefreshPauseStore = create<LiveRefreshPauseState>()((set) => ({
    pausedBy: new Set(),
    setPaused: (key, paused) => set(({ pausedBy }) => ({ pausedBy: withKey(pausedBy, key, paused) })),
}));

/**
 * Holds the alignment's live refresh while `active`, e.g. while its copy is edited or a comment on it is written, so
 * a refetch does not move it under the user.
 */
export const usePauseLiveRefresh = (alignmentId: string, active: boolean): void => {
    const key = `${alignmentId}:${useId()}`;
    const setPaused = useLiveRefreshPauseStore((state) => state.setPaused);

    useEffect(() => {
        setPaused(key, active);

        return () => setPaused(key, false);
    }, [active, key, setPaused]);
};

/** The polling options for a query: every few seconds while visible, never while its alignment is held. */
export const useLiveRefreshOptions = (
    alignmentId: string | null,
): {
    readonly refetchInterval: number | false;
    readonly refetchOnWindowFocus: boolean;
} => {
    const hold = useLiveRefreshPauseStore(
        ({ pausedBy }) => alignmentId !== null && [...pausedBy].some((key) => key.startsWith(`${alignmentId}:`)),
    );

    return { refetchInterval: hold ? false : LIVE_REFRESH_INTERVAL_MS, refetchOnWindowFocus: !hold };
};
